import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import Fields from '../../lib/fields';
import FormFieldsCompact from '../form/fieldsCompact';
import ChipPicker from './chipPicker';
import HomePicker from './homePicker';
import AlertHistory from './alertHistory';
import NsfCompetitionList from './list';
import { Channel } from './inviteDrawer';

// «Varsle meg» bottom drawer: the user's watches (list / edit / alert history).
// Scalar fields are FormFieldsCompact fields from fields-other.js; the three name
// lists are ChipPickers saved through the store (arrays replace on PATCH).
const SCALAR_FIELDS = ['name', 'maxDistanceKm', 'notifyPush', 'notifyEmail', 'weeklyDigest', 'active'];
const TABS = ['list', 'edit', 'alerts'];

/**
 * Body for POST /api/nsfcompetitionwatches/. The store home (updated by saveHome in this
 * session) wins over userStore.user, which is not refreshed after a profile save.
 */
export function watchInputFromPrefill({
    prefill = {}, home = null, user = {}, hostname = '',
}) {
    const input = {
        hostname,
        name: prefill.name || '',
        branchNames: prefill.branchNames || [],
        disciplineGroups: prefill.disciplineGroups || [],
        kretsNames: prefill.kretsNames || [],
    };
    if (home && Number.isFinite(home.lat) && Number.isFinite(home.lng)) {
        input.homeLat = home.lat;
        input.homeLng = home.lng;
        input.homeLabel = home.label || '';
    } else if (user && Number.isFinite(user.homeLat) && Number.isFinite(user.homeLng)) {
        input.homeLat = user.homeLat;
        input.homeLng = user.homeLng;
        input.homeLabel = user.homeLabel || '';
    }
    return input;
}

@observer
class NsfCompetitionWatchDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = { tab: 'list', busy: false };
    }

    componentDidMount() {
        const { stores, prefill } = this.props;
        const { nsfCompetitionWatchStore, nsfCompetitionStore, userStore } = stores;
        if (!this.loggedIn()) {
            return;
        }
        nsfCompetitionWatchStore.setOptions(nsfCompetitionStore.filterOptions);
        nsfCompetitionWatchStore.loadWatches();
        nsfCompetitionWatchStore.loadAlerts();
        const user = userStore.user || {};
        if (Number.isFinite(user.homeLat) && Number.isFinite(user.homeLng)) {
            nsfCompetitionWatchStore.setHome({ lat: user.homeLat, lng: user.homeLng, label: user.homeLabel || '' });
        }
        if (prefill) {
            this.createWatch(prefill);
        }
    }

    loggedIn() {
        const { userStore } = this.props.stores;
        return !!(userStore && userStore.user && userStore.user.id);
    }

    hostname() {
        return typeof window !== 'undefined' ? window.location.hostname : '';
    }

    createWatch = async (prefill = {}) => {
        const { nsfCompetitionWatchStore, userStore } = this.props.stores;
        const input = watchInputFromPrefill({
            prefill, home: nsfCompetitionWatchStore.home, user: userStore.user || {}, hostname: this.hostname(),
        });
        this.setState({ busy: true });
        const created = await nsfCompetitionWatchStore.createWatch(input);
        this.setState({ busy: false });
        if (created) {
            this.setState({ tab: 'edit' });
        }
    }

    editWatch = (id) => {
        const { nsfCompetitionWatchStore } = this.props.stores;
        nsfCompetitionWatchStore.selectWatch(id);
        this.setState({ tab: 'edit' });
    }

    deleteWatch = async (id) => {
        const { nsfCompetitionWatchStore } = this.props.stores;
        await nsfCompetitionWatchStore.deleteWatch(id);
        this.setState({ tab: 'list' });
    }

    toggleActive = async (watch) => {
        const { nsfCompetitionWatchStore } = this.props.stores;
        await nsfCompetitionWatchStore.saveField(watch.id, 'active', watch.active === 0 ? 1 : 0, true);
    }

    toggleName = (field, name) => {
        const { nsfCompetitionWatchStore } = this.props.stores;
        const watch = nsfCompetitionWatchStore.nsfCompetitionWatch || {};
        if (watch.id) {
            nsfCompetitionWatchStore.toggleListValue(watch.id, field, name);
        }
    }

    showMatches = () => {
        const { nsfCompetitionWatchStore } = this.props.stores;
        const watch = nsfCompetitionWatchStore.nsfCompetitionWatch || {};
        if (watch.id) {
            nsfCompetitionWatchStore.testWatch(watch.id);
        }
    }

    sendTest = () => {
        const { nsfCompetitionWatchStore } = this.props.stores;
        const watch = nsfCompetitionWatchStore.nsfCompetitionWatch || {};
        if (watch.id) {
            nsfCompetitionWatchStore.sendTest(watch.id);
        }
    }

    done = () => {
        const { nsfCompetitionWatchStore } = this.props.stores;
        nsfCompetitionWatchStore.loadWatches();
        this.setState({ tab: 'list' });
    }

    close = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    setTab = (tab) => {
        if (TABS.includes(tab)) {
            this.setState({ tab });
        }
    }

    renderList() {
        const { nsfCompetitionWatchStore } = this.props.stores;
        const { nsfCompetitionWatches = [] } = nsfCompetitionWatchStore;
        const { busy } = this.state;
        return (
            <div>
                <div class='d-flex justify-content-between align-items-center mb-2'>
                    <h6 class='mb-0'><Text id='terminliste.myWatches'>My watches</Text></h6>
                    <button type='button' class='btn btn-sm btn-success' disabled={busy} onClick={() => this.createWatch({})}>
                        <i class='fa-solid fa-plus me-1' />
                        <Text id='terminliste.newWatch'>New watch</Text>
                    </button>
                </div>
                {nsfCompetitionWatches.length === 0 && (
                    <div class='small text-body-secondary'><Text id='terminliste.noWatches'>You have no watches yet.</Text></div>
                )}
                <div class='list-group'>
                    {nsfCompetitionWatches.map(watch => (
                        <div key={watch.id} class={`list-group-item d-flex align-items-center gap-2 ${watch.active === 0 ? 'opacity-50' : ''}`}>
                            <div class='form-check form-switch mb-0'>
                                <input class='form-check-input' type='checkbox' checked={watch.active !== 0} onChange={() => this.toggleActive(watch)} />
                            </div>
                            <div class='flex-grow-1'>
                                <div class='fw-semibold'>{watch.name || <Text id='terminliste.newWatch'>New watch</Text>}</div>
                                <div class='small text-body-secondary'>
                                    {[
                                        (watch.branchNames || []).join('/'),
                                        (watch.disciplineGroups || []).join('/'),
                                        (watch.kretsNames || []).join('/'),
                                        watch.maxDistanceKm > 0 ? `≤ ${watch.maxDistanceKm} km` : '',
                                    ].filter(Boolean).join(' · ') || <Text id='terminliste.any'>Any</Text>}
                                </div>
                            </div>
                            <button type='button' class='btn btn-sm btn-outline-primary' onClick={() => this.editWatch(watch.id)}>
                                <Text id='terminliste.edit'>Edit</Text>
                            </button>
                            <button type='button' class='btn btn-sm btn-outline-danger' onClick={() => this.deleteWatch(watch.id)}>
                                <i class='fa-solid fa-trash' />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    renderEdit() {
        const { stores, drawerLevel } = this.props;
        const { nsfCompetitionWatchStore } = stores;
        const watch = nsfCompetitionWatchStore.nsfCompetitionWatch || {};
        if (!watch.id) {
            return this.renderList();
        }
        const sections = Fields.get('nsfCompetitionWatch');
        const {
            testMatches = [], testing, testSending, testSendResult,
        } = nsfCompetitionWatchStore;
        return (
            <div>
                <div class='small text-body-secondary mb-2'>
                    <i class='fa-solid fa-cloud-check me-1' />
                    <Text id='terminliste.autoSaved'>Changes are saved automatically.</Text>
                </div>
                <FormFieldsCompact
                    stores={stores}
                    sections={sections}
                    fields={SCALAR_FIELDS}
                    holdingStore={nsfCompetitionWatchStore}
                    objectName='nsfCompetitionWatch'
                    drawerLevel={drawerLevel}
                />
                <div class='mb-2'>
                    <div class='small fw-semibold mb-1'><Text id='terminliste.branch'>Gren</Text></div>
                    <ChipPicker options={nsfCompetitionWatchStore.branchNameOptions} selected={watch.branchNames} onToggle={name => this.toggleName('branchNames', name)} />
                </div>
                <div class='mb-2'>
                    <div class='small fw-semibold mb-1'><Text id='terminliste.disciplineGroup'>Discipline group</Text></div>
                    <ChipPicker options={nsfCompetitionWatchStore.disciplineGroupOptions} selected={watch.disciplineGroups} onToggle={name => this.toggleName('disciplineGroups', name)} />
                </div>
                <div class='mb-3'>
                    <div class='small fw-semibold mb-1'><Text id='terminliste.krets'>Krets</Text></div>
                    <ChipPicker options={nsfCompetitionWatchStore.kretsNameOptions} selected={watch.kretsNames} onToggle={name => this.toggleName('kretsNames', name)} />
                </div>
                <div class='mb-3'>
                    <div class='small fw-semibold mb-1'><Text id='terminliste.home'>Home position</Text></div>
                    <HomePicker stores={stores} watch={watch} />
                </div>
                <div class='d-flex gap-2 mb-2'>
                    <button type='button' class='btn btn-sm btn-outline-primary' disabled={testing} onClick={this.showMatches}>
                        <i class='fa-duotone fa-magnifying-glass me-1' />
                        <Text id='terminliste.showMatches'>Show matches</Text>
                    </button>
                    <button type='button' class='btn btn-sm btn-outline-secondary nsf-send-test' disabled={testSending} onClick={this.sendTest}>
                        <i class={`fa-duotone ${testSending ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-1`} />
                        <Text id='terminliste.sendTest'>Send test alert</Text>
                    </button>
                    <button type='button' class='btn btn-sm btn-success ms-auto' onClick={this.done}>
                        <Text id='terminliste.done'>Done</Text>
                    </button>
                </div>
                {testSendResult && (
                    <div class={`alert ${testSendResult.error ? 'alert-danger' : 'alert-success'} py-2 small nsf-send-test-result`}>
                        {testSendResult.error ? testSendResult.error : (
                            <span>
                                <Text id='terminliste.testSent'>Test alert sent</Text>
                                {testSendResult.sample ? ` (${testSendResult.sample.title})` : ''}.
                                {' '}Push: <Channel value={testSendResult.push} />
                                {' · '}<Text id='terminliste.invite.email'>E-mail</Text>: <Channel value={testSendResult.email} />
                                {testSendResult.pushError ? ` (${testSendResult.pushError})` : ''}
                                {testSendResult.emailError ? ` (${testSendResult.emailError})` : ''}
                            </span>
                        )}
                    </div>
                )}
                {testMatches.length > 0 && (
                    <div>
                        <div class='small fw-semibold mb-1'><Text id='terminliste.matches'>Competitions matching now</Text></div>
                        <div style='max-height: 40vh; overflow: auto;'>
                            <NsfCompetitionList rows={testMatches} />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { stores } = this.props;
        const { tab } = this.state;
        if (!this.loggedIn()) {
            return (
                <div class='container py-3'>
                    <div class='alert alert-info'>
                        <Text id='terminliste.loginToWatch'>Log in to set up alerts.</Text>
                    </div>
                </div>
            );
        }
        const { nsfCompetitionWatchStore } = stores;
        return (
            <div class='container py-3' style='padding-bottom: 80px;'>
                <div class='d-flex justify-content-between align-items-center mb-3'>
                    <h5 class='mb-0'><Text id='terminliste.watchMe'>Alert me</Text></h5>
                    <button type='button' class='btn btn-sm btn-outline-secondary' onClick={this.close}>
                        <i class='fa-solid fa-xmark' />
                    </button>
                </div>
                <ul class='nav nav-pills mb-3'>
                    <li class='nav-item'>
                        <button type='button' class={`nav-link ${tab === 'list' ? 'active' : ''}`} onClick={() => this.setTab('list')}>
                            <Text id='terminliste.myWatches'>My watches</Text>
                        </button>
                    </li>
                    <li class='nav-item'>
                        <button type='button' class={`nav-link ${tab === 'edit' ? 'active' : ''}`} onClick={() => this.setTab('edit')}>
                            <Text id='terminliste.edit'>Edit</Text>
                        </button>
                    </li>
                    <li class='nav-item'>
                        <button type='button' class={`nav-link ${tab === 'alerts' ? 'active' : ''}`} onClick={() => this.setTab('alerts')}>
                            <Text id='terminliste.alertHistory'>Alerts</Text>
                        </button>
                    </li>
                </ul>
                {tab === 'list' && this.renderList()}
                {tab === 'edit' && this.renderEdit()}
                {tab === 'alerts' && <AlertHistory alerts={nsfCompetitionWatchStore.alerts} />}
            </div>
        );
    }
}

export default NsfCompetitionWatchDrawer;
